'use client'

import { SessionProvider } from 'next-auth/react'
import { ThemeProvider } from 'next-themes'
import { ToastProvider } from '@/components/ui/toast'
import { ChatbotProvider } from '@/context/chatbot-context'
import { TestModeProvider } from '@/contexts/test-mode-context'
import GlobalAIChatbot from '@/components/global/global-ai-chatbot'
import { useTheme } from 'next-themes'
import { useEffect } from 'react'
import {
  Chart as ChartJS,
  Filler,
  Legend,
  Tooltip,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  TimeScale,
} from 'chart.js'

ChartJS.register(Filler, Legend, Tooltip, CategoryScale, LinearScale, PointElement, LineElement, BarElement, TimeScale)

function useBrandChartTheme() {
  const { resolvedTheme } = useTheme()
  useEffect(() => {
    const isDark = resolvedTheme === 'dark'
    ChartJS.defaults.color = isDark ? '#F5F9FC' : '#1a202c'
    ChartJS.defaults.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
    ChartJS.defaults.font.family = 'var(--font-sans), ui-sans-serif, system-ui'
    // Tooltip
    ChartJS.defaults.plugins.tooltip.backgroundColor = isDark ? 'rgba(21, 33, 44, 0.95)' : 'rgba(255, 255, 255, 0.95)'
    ChartJS.defaults.plugins.tooltip.titleColor = isDark ? '#F5F9FC' : '#1a202c'
    ChartJS.defaults.plugins.tooltip.bodyColor = isDark ? '#F5F9FC' : '#374151'
    ChartJS.defaults.plugins.tooltip.borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
    ChartJS.defaults.plugins.tooltip.borderWidth = isDark ? 0 : 1
    // Legend
    ChartJS.defaults.plugins.legend.labels.color = isDark ? '#F5F9FC' : '#374151'
    // @ts-ignore
    ChartJS.defaults.elements.line = { borderWidth: 2, borderDash: [], borderDashOffset: 0 }
    // @ts-ignore
    ChartJS.defaults.elements.point = { radius: 3, hoverRadius: 4 }
    // Force re-render of all charts
    ChartJS.instances && Object.values(ChartJS.instances).forEach((chart: any) => chart?.update?.())
  }, [resolvedTheme])
}

interface ProvidersProps {
  children: React.ReactNode
  session: any
}

function ChartThemeSync({ children }: { children: React.ReactNode }) {
  useBrandChartTheme()
  return <>{children}</>
}

export function Providers({ children, session }: ProvidersProps) {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} disableTransitionOnChange>
      <SessionProvider session={session}>
        <TestModeProvider>
          <ToastProvider>
            <ChatbotProvider>
              <ChartThemeSync>
                {children}
                <GlobalAIChatbot />
              </ChartThemeSync>
            </ChatbotProvider>
          </ToastProvider>
        </TestModeProvider>
      </SessionProvider>
    </ThemeProvider>
  )
} 